/**
 * Prompts for AI Organizer Insights (Phase 2I).
 *
 * Every number the narrative may mention is produced deterministically by the
 * `ai-organizer-insights` statistics endpoint. The model narrates — it never
 * computes, invents, extrapolates or contradicts a figure, and it never sees
 * attendee data or another organizer's data.
 *
 * Prompt engineering is a product concern — no vendor names, no transport
 * details, no model ids in this file.
 */

import type { OrganizerInsightsStats } from '../types';

export const INSIGHTS_LIMITS = {
  /** ~200 tokens of answer plus headroom for reasoning/formatting. */
  maxOutputTokens: 700,
  maxSummaryChars: 600,
  maxActionChars: 200,
  /** Input guards — the prompt must stay small and bounded. */
  maxCategories: 8,
  maxRuleInsights: 6,
  maxTitleChars: 120,
} as const;

const money = (value: number | null | undefined, currency: string): string =>
  value === null || value === undefined || !Number.isFinite(value)
    ? 'unknown'
    : `${currency} ${value.toFixed(2)}`;

const percent = (value: number | null | undefined): string =>
  value === null || value === undefined || !Number.isFinite(value)
    ? 'unknown'
    : `${Math.round(value * 100)}%`;

const clamp = (value: string, max: number): string =>
  value.length > max ? `${value.slice(0, max - 1).trimEnd()}…` : value;

export interface InsightsPromptInput {
  stats: OrganizerInsightsStats;
  currency: string;
  language: string;
}

export function insightsSystemPrompt(input: InsightsPromptInput): string {
  return [
    'You are an event business growth advisor for an event ticketing platform.',
    'You are given pre-computed performance statistics for a single organizer. Treat them as ground truth.',
    'NEVER invent, recompute, extrapolate or contradict a number: only reference figures present in the data.',
    'Never mention or identify other organizers, competing events, venues, attendees or any personal data.',
    `Write in the language with code "${input.language}".`,
    `All money amounts use the currency code "${input.currency}".`,
    'Respond with a JSON object exactly of the form:',
    '{"summary": string, "topAction": string}',
    '"summary" is a specific, actionable growth summary of at most three sentences.',
    '"topAction" is one short sentence naming the single most impactful next step.',
    'Do not wrap the JSON in code fences and do not add any other keys.',
  ].join(' ');
}

export function insightsUserPrompt(input: InsightsPromptInput): string {
  const { stats, currency } = input;
  const lines = [
    `Total events created: ${stats.totalEvents}.`,
    `Active (published, upcoming) events: ${stats.activeEvents}.`,
    `Total revenue to date: ${money(stats.totalRevenue, currency)}.`,
    `Average sell-through across published events: ${percent(stats.avgSellThrough)}.`,
    `Highest-revenue event: ${
      stats.bestEventTitle ? `"${clamp(stats.bestEventTitle, INSIGHTS_LIMITS.maxTitleChars)}"` : 'not available'
    }.`,
  ];

  const categories = stats.categories.slice(0, INSIGHTS_LIMITS.maxCategories);
  if (categories.length) {
    lines.push('Category performance:');
    for (const category of categories) {
      lines.push(
        `- ${clamp(category.name, INSIGHTS_LIMITS.maxTitleChars)}: ${percent(
          category.sellThrough,
        )} sell-through across ${category.events} event(s).`,
      );
    }
  }

  const ruleInsights = stats.ruleInsights.slice(0, INSIGHTS_LIMITS.maxRuleInsights);
  if (ruleInsights.length) {
    lines.push('Signals already detected by the platform (do not repeat them verbatim):');
    for (const insight of ruleInsights) {
      lines.push(`- [${insight.severity}] ${clamp(insight.title, INSIGHTS_LIMITS.maxTitleChars)}`);
    }
  }

  if (stats.totalEvents === 0) {
    lines.push('IMPORTANT: there is no performance history yet. Give general first-event guidance.');
  }

  return lines.join('\n');
}
